import { useEffect } from "react";

import { isTourInteractiveTarget } from "@/components/onboarding/utils/tourInteractive";

/**
 * The z-index for the click blocker, placed below the spotlight and tooltip.
 * Must sit ABOVE the app's modal layer (`.modal`, z-index 1000) — the Dataset
 * Explorer renders as a modal, so a blocker below 1000 would leave the modal
 * content fully clickable.
 */
const Z_INDEX = 1010; // Below spotlight (1011) and tooltip (1012), above .modal (1000).

const blockerStyle: React.CSSProperties = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: "transparent",
  zIndex: Z_INDEX
};

const CLICK_SUPPRESSION_MS = 1000;

const stopEvent = (event: Event) => {
  event.preventDefault();
  event.stopPropagation();
  event.stopImmediatePropagation();
};

/**
 * Captures pointer events across the entire viewport while the tour is active so
 * nothing on the page underneath is clickable. The fixed element handles normal
 * stacking, while the document-level capture guard covers higher z-index portals
 * and map controls. Tour tooltip controls remain interactive.
 **/
export function TourClickBlocker() {
  useEffect(() => {
    let shouldBlockNextClick = false;
    let resetClickBlockTimeout: number | undefined;

    const markClickForBlocking = () => {
      shouldBlockNextClick = true;
      window.clearTimeout(resetClickBlockTimeout);
      resetClickBlockTimeout = window.setTimeout(() => {
        shouldBlockNextClick = false;
      }, CLICK_SUPPRESSION_MS);
    };

    const blockPointerEvent = (event: Event) => {
      if (isTourInteractiveTarget(event.target)) return;

      markClickForBlocking();
      stopEvent(event);
    };

    const blockClickEvent = (event: Event) => {
      if (isTourInteractiveTarget(event.target)) return;
      if (!shouldBlockNextClick) return;

      shouldBlockNextClick = false;
      window.clearTimeout(resetClickBlockTimeout);
      stopEvent(event);
    };

    const pointerEventNames = [
      "pointerdown",
      "pointerup",
      "mousedown",
      "mouseup",
      "touchstart",
      "touchend"
    ];
    const clickEventNames = ["click", "dblclick", "auxclick", "contextmenu"];

    pointerEventNames.forEach((eventName) => {
      document.addEventListener(eventName, blockPointerEvent, {
        capture: true,
        passive: false
      });
    });
    clickEventNames.forEach((eventName) => {
      document.addEventListener(eventName, blockClickEvent, true);
    });

    return () => {
      window.clearTimeout(resetClickBlockTimeout);
      pointerEventNames.forEach((eventName) => {
        document.removeEventListener(eventName, blockPointerEvent, true);
      });
      clickEventNames.forEach((eventName) => {
        document.removeEventListener(eventName, blockClickEvent, true);
      });
    };
  }, []);

  return (
    <div aria-hidden data-testid="tour-click-blocker" style={blockerStyle} />
  );
}
